"use strict";
import { useQuery } from "@tanstack/react-query";
import { fetchFromVeSubgraph, isVeSubgraphAvailable } from "features/veSubgraph/client";
import { useMemo } from "react";
const TOKEN_PRICES_QUERY = `
  query TokenPrices($ids: [ID!]!) {
    bundle(id: "1") {
      bnbPriceUSD
    }
    tokens(where: { id_in: $ids }) {
      id
      derivedBNB
      priceSource
    }
  }
`;
export async function fetchVeTokenPricesUsd(chainId, addresses, options = {}) {
  const ids = Array.from(new Set(addresses.filter(Boolean).map((a) => a.toLowerCase())));
  const prices = /* @__PURE__ */ new Map();
  if (ids.length === 0) {
    return prices;
  }
  const data = await fetchFromVeSubgraph(chainId, {
    query: TOKEN_PRICES_QUERY,
    variables: { ids },
    requestId: `veTokenPrices:${chainId}`,
    signal: options.signal
  });
  const bnbPriceUsd = parseFloat(data?.bundle?.bnbPriceUSD ?? "0");
  if (!Number.isFinite(bnbPriceUsd) || bnbPriceUsd <= 0) {
    return prices;
  }
  for (const token of data?.tokens ?? []) {
    if (token.priceSource === "NONE") {
      continue;
    }
    if (options.onlyClPriced && token.priceSource !== "CL") {
      continue;
    }
    const usd = parseFloat(token.derivedBNB ?? "0") * bnbPriceUsd;
    if (Number.isFinite(usd) && usd > 0) {
      prices.set(token.id.toLowerCase(), usd);
    }
  }
  return prices;
}
export function useVeTokenPricesUsd(chainId, addresses) {
  const uniqueAddresses = useMemo(
    () => Array.from(new Set(addresses.filter(Boolean).map((a) => a.toLowerCase()))).sort(),
    [addresses]
  );
  const query = useQuery({
    queryKey: ["ve-token-prices", chainId, uniqueAddresses],
    queryFn: () => fetchVeTokenPricesUsd(chainId, uniqueAddresses),
    enabled: uniqueAddresses.length > 0 && isVeSubgraphAvailable(chainId),
    staleTime: 3e4,
    refetchInterval: 6e4,
    gcTime: 3e5,
    retry: 2
  });
  return useMemo(
    () => ({
      prices: query.data ?? /* @__PURE__ */ new Map(),
      isLoading: query.isLoading,
      isError: query.isError
    }),
    [query.data, query.isLoading, query.isError]
  );
}
export function useVeTokenPriceUsd(chainId, address) {
  const addresses = useMemo(() => address ? [address] : [], [address]);
  const { prices } = useVeTokenPricesUsd(chainId, addresses);
  return address ? prices.get(address.toLowerCase()) : void 0;
}
